import { resolve, type Resolvable } from "@ai-sdk/provider-utils";
import type {
  ChatRequestOptions,
  ChatTransport,
  UIMessage,
  UIMessageChunk,
} from "ai";

/** Initialization options for {@link WebSocketChatTransport}. */
type WebSocketChatTransportInitOptions = {
  /**
   * The URL of the chat backend.
   */
  api: string;

  /**
   * Bearer token to authenticate the WebSocket handshake.
   */
  token?: Resolvable<string>;

  /**
   * The user's time zone, which will be considered by the LLM for the output time strings.
   * Should be in the format of IANA time zones (e.g., "Europe/Berlin").
   * See https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
   *
   * @default UTC
   */
  timeZone?: string;
};

/**
 * Parameters passed to {@link WebSocketChatTransport.sendMessages}.
 *
 * Extends {@link ChatRequestOptions} with the additional fields required to
 * open a WebSocket session and stream a response from the chat backend.
 *
 * @template T - The concrete UI message type used in this chat session.
 */
type SendMessagesParams<T> = ChatRequestOptions & {
  /** Whether the user submitted a new message or is regenerating an existing one. */
  trigger: "submit-message" | "regenerate-message";
  /** Unique identifier of the chat session. */
  chatId: string;
  /** ID of the message being regenerated, or `undefined` for a new submission. */
  messageId: string | undefined;
  /** The full ordered list of messages to send as the conversation context. */
  messages: T[];
  /** Signal used to abort the in-flight request and close the WebSocket. */
  abortSignal: AbortSignal | undefined;
};

/** Chat transport implementation that communicates with the server via WebSocket. */
export class WebSocketChatTransport<
  UiMessage extends UIMessage,
> implements ChatTransport<UiMessage> {
  private url: WebSocketChatTransportInitOptions["api"];
  private token: WebSocketChatTransportInitOptions["token"];
  private timeZone: WebSocketChatTransportInitOptions["timeZone"];

  /** Creates a new {@link WebSocketChatTransport} connected to the given API endpoint. */
  constructor({ api, token, timeZone }: WebSocketChatTransportInitOptions) {
    this.url = api;
    this.token = token;
    this.timeZone = timeZone;
  }

  /**
   * Opens a WebSocket connection to the configured endpoint, sends the
   * current conversation as the handshake payload, and returns a
   * {@link ReadableStream} that emits {@link UIMessageChunk} objects as they
   * arrive from the server.
   *
   * The stream is closed automatically when the server closes the socket.
   * If the provided `abortSignal` fires the socket is closed immediately and the
   * stream is cancelled.
   *
   * @returns A promise that resolves to a readable stream of message chunks.
   */
  async sendMessages({
    trigger,
    chatId,
    messageId,
    messages,
    abortSignal,
    body,
    metadata,
  }: SendMessagesParams<UiMessage>): Promise<ReadableStream<UIMessageChunk>> {
    const { url: socketUrl, timeZone } = this;
    const bearerToken = await resolve(this.token);

    let websocket: WebSocket | undefined;

    const stream = new ReadableStream<UIMessageChunk>({
      start(controller) {
        const url = new URL(socketUrl, window.location.origin);

        // The provided URL might be using the HTTP(S) protocol, which needs to be fixed
        if (url.protocol === "https:") {
          url.protocol = "wss:";
        } else if (url.protocol === "http:") {
          url.protocol = "ws:";
        }

        const ws = new WebSocket(url);
        websocket = ws;
        let hasErrored = false;

        function closeSocket(): void {
          if (
            ws.readyState === WebSocket.OPEN ||
            ws.readyState === WebSocket.CONNECTING
          ) {
            ws.close();
          }
        }

        function onAbort(): void {
          closeSocket();
          controller.close();
        }

        // Register before the socket is fully open to avoid a race where the
        // signal fires between socket creation and the `onopen` callback.
        abortSignal?.addEventListener("abort", onAbort);

        ws.onopen = () => {
          const initPayload = {
            token: bearerToken,
            trigger,
            chatId,
            messageId,
            messages,
            body,
            metadata,
            timeZone,
          };

          ws.send(JSON.stringify(initPayload));
        };

        ws.onmessage = (event) => {
          // Some browser/server combinations deliver WebSocket frames as Blobs
          // rather than strings, so we need the async text() conversion here.
          if (event.data instanceof Blob) {
            event.data
              .text()
              .then((text) => {
                const chunks = toChunkArray(text);
                for (const chunk of chunks) {
                  controller.enqueue(chunk);
                }
              })
              .catch((error) => {
                hasErrored = true;
                controller.error(error);
                closeSocket();
              });

            return;
          }

          const chunks = toChunkArray(event.data);
          for (const chunk of chunks) {
            controller.enqueue(chunk);
          }
        };

        ws.onerror = () => {
          hasErrored = true;
          controller.error(new Error("WebSocket connection error."));
        };

        ws.onclose = () => {
          abortSignal?.removeEventListener("abort", onAbort);

          // Only close the stream if we haven't already reported an error;
          // closing after erroring would throw inside ReadableStream.
          if (!hasErrored) {
            controller.close();
          }
        };
      },
      cancel() {
        if (
          websocket?.readyState === WebSocket.OPEN ||
          websocket?.readyState === WebSocket.CONNECTING
        ) {
          websocket.close();
        }
      },
    });

    return Promise.resolve(stream);
  }

  /**
   * Reconnecting to an in-progress stream is not supported by this transport.
   *
   * @returns A promise that always resolves to `null`.
   */
  reconnectToStream = (): Promise<ReadableStream<UIMessageChunk> | null> =>
    Promise.resolve(null);
}

/**
 * Normalizes an arbitrary WebSocket message payload into an array of
 * {@link UIMessageChunk} objects.
 *
 * Handles the following shapes:
 * - The sentinel string `"[DONE]"` → empty array
 * - SSE-style `data: ...` lines → recursively parsed
 * - JSON strings → parsed and recursed
 * - Plain arrays → filtered to valid chunks
 * - Objects with a `chunk`, `chunks`, or `data` property → recursed
 * - Objects that are already a valid chunk → wrapped in an array
 *
 * @param data - The raw message payload received from the WebSocket.
 * @returns An array of parsed {@link UIMessageChunk} objects (may be empty).
 */
function toChunkArray(data: unknown): UIMessageChunk[] {
  if (data === "[DONE]") {
    return [];
  }

  if (typeof data === "string") {
    const trimmed = data.trim();

    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith("data:")) {
      const chunks: UIMessageChunk[] = [];
      const lines = trimmed.split(/\r?\n/);

      for (const line of lines) {
        if (!line.startsWith("data:")) {
          continue;
        }

        const payload = line.slice(5).trim();
        if (!payload || payload === "[DONE]") {
          continue;
        }

        chunks.push(...toChunkArray(payload));
      }

      return chunks;
    }

    try {
      const parsed: unknown = JSON.parse(trimmed);
      return toChunkArray(parsed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(data)) {
    return data.filter(isUIMessageChunk);
  }

  if (typeof data === "object" && data !== null) {
    if (isUIMessageChunk(data)) {
      return [data];
    }

    if ("chunk" in data) {
      return toChunkArray(data.chunk);
    }

    if ("chunks" in data) {
      return toChunkArray(data.chunks);
    }

    if ("data" in data) {
      return toChunkArray(data.data);
    }
  }

  return [];
}

/**
 * Type guard that checks whether an unknown value is a {@link UIMessageChunk}.
 *
 * @param item - The value to test.
 * @returns `true` if `item` has the shape of a {@link UIMessageChunk}.
 */
function isUIMessageChunk(item: unknown): item is UIMessageChunk {
  return typeof item === "object" && item !== null && "type" in item;
}
